import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';

const CredencialCard = ({ isOpen, onClose, credencial }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [dots, setDots] = useState([]);

  // Inyectar estilos globales una sola vez
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
      @keyframes pulse-slow {
        0%, 100% {
          opacity: 0.6;
          transform: rotate(45deg) scale(1);
        }
        50% {
          opacity: 1;
          transform: rotate(45deg) scale(1.1);
        }
      }
      .animate-pulse-slow {
        animation: pulse-slow 2s ease-in-out infinite;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Configuración de colores según el tipo
  const getColors = (colorName) => {
    const colors = {
      NARANJA: {
        normal: '#F68C2A',
        fuerte: '#E05A1A',
        claro: '#FBB036',
        gradiente: 'from-[#F68C2A] to-[#E05A1A]',
      },
      AZUL: {
        normal: '#064382',
        fuerte: '#002140',
        claro: '#0065A4',
        gradiente: 'from-[#064382] to-[#002140]',
      },
      AQUA: {
        normal: '#1E9FAB',
        fuerte: '#075F63',
        claro: '#22D1C2',
        gradiente: 'from-[#1E9FAB] to-[#075F63]',
      },
      CELESTE: {
        normal: '#57DFFF',
        fuerte: '#3076D1',
        claro: '#86EBFF',
        gradiente: 'from-[#57DFFF] to-[#3076D1]',
      },
      MORADO: {
        normal: '#766CAF',
        fuerte: '#3F3F92',
        claro: '#BBABD3',
        gradiente: 'from-[#766CAF] to-[#3F3F92]',
      },
      VERDE: {
        normal: '#30AE6E',
        fuerte: '#197338',
        claro: '#6CFFB4',
        gradiente: 'from-[#30AE6E] to-[#197338]',
      },
    };
    return colors[colorName] || colors.AZUL;
  };

  const colorConfig = getColors(credencial?.color || 'AZUL');
  const backgroundColor = colorConfig.normal;
  const accentDark = colorConfig.fuerte;
  const accentNormal = colorConfig.normal;
  const accentLight = colorConfig.claro;

  const accesoTextColor = credencial?.color === 'MORADO' ? accentDark : 'white';
  const accesoBgColor =
    credencial?.color === 'MORADO' ? accentLight : backgroundColor;

  // Generar puntos animados
  useEffect(() => {
    if (isOpen && !isFlipped) {
      const generateDots = () => {
        const newDots = [];
        for (let i = 0; i < 60; i++) {
          newDots.push({
            id: i,
            x: Math.random() * 100,
            y: Math.random() * 100,
            size: Math.random() * 6 + 1,
            delay: Math.random() * 5,
            duration: Math.random() * 4 + 2,
          });
        }
        setDots(newDots);
      };
      generateDots();

      const interval = setInterval(() => {
        setDots((prev) =>
          prev.map((dot) => ({
            ...dot,
            x: Math.random() * 100,
            y: Math.random() * 100,
          }))
        );
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [isOpen, isFlipped]);

  // Obtener el logo según razón social
  const getLogo = () => {
    const razonSocial = credencial?.razon_social || '';
    if (
      razonSocial.includes('PACKSYS MEXICO') ||
      razonSocial.includes('PACKSYS DISTRIBUCION')
    ) {
      return '/media/PACKSYS.png';
    }
    if (razonSocial.includes('THAIBO')) return '/media/THAIBO.png';
    if (razonSocial.includes('REMBEX')) return '/media/REMBEX.png';
    if (razonSocial.includes('DENSEN')) return '/media/DENSEN.png';
    if (razonSocial.includes('INNOVA')) return '/media/INNOVA.png';
    if (razonSocial.includes('HALYCON')) return '/media/HALYCON.png';
    return '/media/PACKSYS.png';
  };

  // Obtener REPSE según razón social (versión hardcodeada para pruebas)
  const getRepse = () => {
    const razonSocial = credencial?.razon_social || '';

    if (razonSocial.includes('THAIBO')) {
      return 'a1633bd0-518e-44b2-8f8c-2aa188ea8854';
    }
    if (razonSocial.includes('REMBEX')) {
      return 'd0f8da18-8938-4410-b9ee-de0e4ac1de09';
    }
    if (razonSocial.includes('DENSEN')) {
      return 'f2b879eb-b0de-4e8d-87a6-759fdf611d80';
    }
    if (razonSocial.includes('INNOVA')) {
      return 'ed545b30-e300-4bd3-b448-2be0621e33e4';
    }
    return null;
  };

  // Determinar si debe mostrar REPSE
  const shouldShowRepse = () => {
    const razonSocial = credencial?.razon_social || '';
    return (
      razonSocial.includes('THAIBO') ||
      razonSocial.includes('REMBEX') ||
      razonSocial.includes('DENSEN') ||
      razonSocial.includes('INNOVA')
    );
  };

  // Determinar si es del grupo Packsys/Halycon
  const isPacksysGroup = () => {
    const razonSocial = credencial?.razon_social || '';
    return (
      razonSocial.includes('PACKSYS MEXICO') ||
      razonSocial.includes('PACKSYS DISTRIBUCION') ||
      razonSocial.includes('HALYCON')
    );
  };

  // Validación después de todos los hooks
  if (!credencial) return null;

  // Componente de rombo reutilizable
  const Diamond = ({ size = 6, color, className = '', animate = false }) => (
    <div
      className={`${animate ? 'animate-pulse-slow' : ''} ${className}`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        transform: 'rotate(45deg)',
        backgroundColor: color,
        boxShadow: `0 0 8px ${color}`,
      }}
    />
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-400"
              enterFrom="opacity-0 scale-90 rotate-[-5deg]"
              enterTo="opacity-100 scale-100 rotate-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 rotate-0"
              leaveTo="opacity-0 scale-90 rotate-[5deg]"
            >
              <Dialog.Panel className="w-full max-w-md transform transition-all">
                <div
                  className="relative w-full cursor-pointer"
                  style={{ perspective: '2000px' }}
                  onClick={() => setIsFlipped(!isFlipped)}
                >
                  <div
                    className="relative w-full transition-all duration-700 ease-out"
                    style={{
                      transformStyle: 'preserve-3d',
                      transform: isFlipped
                        ? 'rotateY(180deg)'
                        : 'rotateY(0deg)',
                      position: 'relative',
                    }}
                  >
                    {/* FRONTAL */}
                    <div
                      className="relative w-full rounded-2xl overflow-hidden shadow-2xl"
                      style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden', // Para Safari/iOS
                        position: 'relative',
                        background: `linear-gradient(135deg, ${backgroundColor} 0%, ${accentDark} 100%)`,
                        height: '580px',
                        zIndex: isFlipped ? 0 : 2, // Importante: ocultar cuando está volteada
                      }}
                    >
                      {/* Fondo animado de puntos */}
                      <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        {dots.map((dot) => (
                          <div
                            key={dot.id}
                            className="absolute rounded-full transition-all duration-1000"
                            style={{
                              left: `${dot.x}%`,
                              top: `${dot.y}%`,
                              width: `${dot.size}px`,
                              height: `${dot.size}px`,
                              backgroundColor: accentLight,
                              opacity: 0.2 + Math.random() * 0.3,
                              animation: `float ${dot.duration}s ease-in-out infinite`,
                              animationDelay: `${dot.delay}s`,
                            }}
                          />
                        ))}
                      </div>

                      {/* ROMBOS DECORATIVOS */}
                      <div className="absolute top-4 left-4 flex gap-1">
                        <Diamond size={8} color={accentLight} animate />
                        <Diamond size={12} color="white" />
                        <Diamond size={6} color={accentLight} animate />
                      </div>

                      <div className="absolute top-4 right-4 flex gap-1">
                        <Diamond size={6} color={accentLight} animate />
                        <Diamond size={12} color="white" />
                        <Diamond size={8} color={accentLight} animate />
                      </div>

                      <div className="absolute bottom-4 left-4 flex gap-1">
                        <Diamond size={8} color={accentLight} animate />
                        <Diamond size={10} color="white" />
                        <Diamond size={6} color={accentLight} animate />
                      </div>

                      <div className="absolute bottom-4 right-4 flex gap-1">
                        <Diamond size={6} color={accentLight} animate />
                        <Diamond size={10} color="white" />
                        <Diamond size={8} color={accentLight} animate />
                      </div>

                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 flex flex-col gap-2">
                        {[...Array(5)].map((_, i) => (
                          <Diamond
                            key={i}
                            size={5}
                            color="white"
                            animate={i % 2 === 0}
                          />
                        ))}
                      </div>

                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex flex-col gap-2">
                        {[...Array(5)].map((_, i) => (
                          <Diamond
                            key={i}
                            size={5}
                            color="white"
                            animate={i % 2 === 0}
                          />
                        ))}
                      </div>

                      {/* ROMBO CENTRAL GRANDE */}
                      <div
                        className="absolute"
                        style={{
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%) rotate(45deg)',
                          width: '280px',
                          height: '280px',
                          border: `30px solid ${accentLight}`,
                          opacity: 0.1,
                          pointerEvents: 'none',
                        }}
                      />

                      {/* Efecto de brillo superior */}
                      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />

                      {/* Contenido frontal */}
                      <div className="relative p-6 flex flex-col items-center text-center z-10 h-full">
                        {/* Logo ovalado */}
                        <div
                          className="absolute left-1/2 transform -translate-x-1/2 w-64 h-20 bg-white rounded-b-xl flex items-center justify-center shadow-xl z-10 overflow-hidden"
                          style={{ top: '-4px' }}
                        >
                          <img
                            src="/media/PACKSYS.png"
                            alt="Packsys"
                            className="p-3 object-contain max-h-full relative z-10"
                          />
                        </div>

                        {/* Acceso */}
                        <div className="relative mt-[72px] mb-4 z-10">
                          <div
                            className="absolute inset-0 rounded-full blur-md"
                            style={{ background: accentDark, opacity: 0.5 }}
                          />
                          <div
                            className="relative px-6 py-2 rounded-full font-bold text-lg tracking-wide shadow-lg"
                            style={{
                              backgroundColor: accesoBgColor,
                              color: accesoTextColor,
                              border: `2px solid ${accentDark}`,
                            }}
                          >
                            {credencial.acceso || 'ACCESO'}
                          </div>
                        </div>

                        {/* Foto */}
                        <div className="flex items-start gap-4 mb-6 relative z-10">
                          <div className="relative">
                            <div className="absolute -inset-1 bg-white/30 rounded-lg blur-sm" />
                            <div className="relative w-44 h-44 overflow-hidden rounded-lg border-4 border-white shadow-2xl">
                              {credencial.foto_url &&
                              credencial.foto_url !== 'null' ? (
                                <img
                                  src={credencial.foto_url}
                                  alt={credencial.nombre_completo}
                                  className="w-full h-full object-cover"
                                  crossOrigin="anonymous"
                                />
                              ) : (
                                <img
                                  src="/media/Credencial_Default.jpg"
                                  alt={credencial.nombre_completo}
                                  className="w-full h-full object-cover"
                                  crossOrigin="anonymous"
                                />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Nombre */}
                        <div className="text-white mb-4 space-y-0.5 relative z-10">
                          <div className="font-bold text-2xl tracking-wide drop-shadow-lg">
                            {credencial.nombres}
                          </div>
                          <div className="font-bold text-2xl tracking-wide drop-shadow-lg">
                            {credencial.apellido_paterno}{' '}
                            {credencial.apellido_materno}
                          </div>
                        </div>

                        {/* Puesto */}
                        <div className="relative mt-4 mb-4 z-10">
                          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-12 h-0.5 bg-white/60 rounded-full" />
                          <div className="text-white/90 text-sm font-medium px-4 py-1 rounded-full bg-white/30 backdrop-blur-sm">
                            {credencial.puesto || 'Puesto no especificado'}
                          </div>
                        </div>

                        {/* Empresa */}
                        <div className="text-white/80 text-sm font-medium tracking-wide uppercase mt-[2px] z-10">
                          {credencial.razon_social ||
                            'PACKSYS MEXICO S.A DE C.V.'}
                        </div>

                        {/* Indicador de voltear */}
                        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10">
                          <div className="text-white/40 text-xs animate-pulse">
                            ← Haz clic para voltear →
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* TRASERA */}
                    <div
                      className="absolute top-0 left-0 w-full rounded-2xl overflow-hidden shadow-2xl"
                      style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        backgroundColor: 'white',
                        height: '580px',
                        zIndex: isFlipped ? 2 : 0, // Mostrar solo cuando está volteada
                      }}
                    >
                      <div className="absolute inset-0 opacity-5 pointer-events-none">
                        <div
                          className="w-full h-full"
                          style={{
                            backgroundImage: `radial-gradient(circle at 2px 2px, ${accentDark} 1px, transparent 1px)`,
                            backgroundSize: '20px 20px',
                          }}
                        />
                      </div>

                      {/* ROMBOS EN TRASERA */}
                      <div className="absolute top-4 left-4 flex gap-1">
                        <Diamond size={6} color={accentDark} animate />
                        <Diamond size={8} color={accentDark} />
                        <Diamond size={6} color={accentDark} animate />
                      </div>
                      <div className="absolute top-4 right-4 flex gap-1">
                        <Diamond size={6} color={accentDark} animate />
                        <Diamond size={8} color={accentDark} />
                        <Diamond size={6} color={accentDark} animate />
                      </div>
                      <div className="absolute bottom-4 left-4 flex gap-1">
                        <Diamond size={6} color={accentDark} animate />
                        <Diamond size={8} color={accentDark} />
                        <Diamond size={6} color={accentDark} animate />
                      </div>
                      <div className="absolute bottom-4 right-4 flex gap-1">
                        <Diamond size={6} color={accentDark} animate />
                        <Diamond size={8} color={accentDark} />
                        <Diamond size={6} color={accentDark} animate />
                      </div>

                      {/* ROMBO CENTRAL GRANDE */}
                      <div
                        className="absolute"
                        style={{
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%) rotate(45deg)',
                          width: '280px',
                          height: '280px',
                          border: `30px solid ${accentLight}`,
                          opacity: 0.1,
                          pointerEvents: 'none',
                        }}
                      />

                      <div className="p-6 flex flex-col items-center text-center h-full relative z-10">
                        <div className="mb-6 transform transition-transform hover:scale-105">
                          <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent blur-md" />
                            <img
                              src={getLogo()}
                              alt="Logo empresa"
                              className="h-16 object-contain relative"
                            />
                          </div>
                        </div>

                        {isPacksysGroup() ? (
                          <div className="text-left w-full space-y-3">
                            <p className="text-gray-600 text-sm leading-relaxed">
                              La siguiente credencial acredita al titular como
                              trabajador asignado a la empresa:
                            </p>
                            <div
                              className="bg-gradient-to-r from-gray-50 to-white p-3 rounded-lg border-l-4 border-2 text-center"
                              style={{
                                borderLeftColor: accentDark,
                                borderColor: accentDark,
                              }}
                            >
                              <p
                                className="font-bold text-lg"
                                style={{ color: accentDark }}
                              >
                                {credencial.razon_social ||
                                  'PACKSYS MEXICO S.A DE C.V.'}
                              </p>
                            </div>
                            <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                              <span className="text-gray-700 text-sm uppercase font-semibold">
                                Registro Patronal:
                              </span>
                              <span
                                className="font-medium"
                                style={{ color: accentNormal }}
                              >
                                {credencial.registro_patronal ||
                                  'No especificado'}
                              </span>
                            </div>
                            <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                              <span className="text-gray-700 text-sm uppercase font-semibold">
                                NSS:
                              </span>
                              <span
                                className="font-medium"
                                style={{ color: accentNormal }}
                              >
                                {credencial.nss || 'No especificado'}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center w-full space-y-4">
                            <div
                              className="bg-gradient-to-r from-gray-50 to-white p-2 rounded-xl border-2"
                              style={{ borderColor: accentDark }}
                            >
                              <p
                                className="font-bold text-sm uppercase tracking-wider mb-1"
                                style={{ color: accentDark }}
                              >
                                Registro Patronal
                              </p>
                              <p className="text-gray-700 font-medium">
                                {credencial.registro_patronal ||
                                  'No especificado'}
                              </p>
                            </div>
                            {/* Campo REPSE - Solo para THAIBO, REMBEX, DENSEN, INNOVA */}
                            {shouldShowRepse() && (
                              <div
                                className="bg-gradient-to-r from-gray-50 to-white p-2 rounded-xl border-2"
                                style={{ borderColor: accentDark }}
                              >
                                <p
                                  className="font-bold text-sm uppercase tracking-wider mb-1"
                                  style={{ color: accentDark }}
                                >
                                  REPSE
                                </p>
                                <p className="text-gray-700 font-mono text-sm break-all">
                                  {getRepse()}
                                </p>
                              </div>
                            )}

                            <div
                              className="bg-gradient-to-r from-gray-50 to-white p-2 rounded-xl border-2"
                              style={{ borderColor: accentDark }}
                            >
                              <p
                                className="font-bold text-sm uppercase tracking-wider mb-1"
                                style={{ color: accentDark }}
                              >
                                NSS
                              </p>
                              <p className="text-gray-700 font-medium">
                                {credencial.nss || 'No especificado'}
                              </p>
                            </div>

                            <div className="mt-4 pt-3 border-t-2 border-[#c9ced8]">
                              <p className="text-[#778194] text-xs font-semibold tracking-wide">
                                Personal asignado a Packsys
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="flex gap-3 mt-auto pt-4">
                          {[...Array(3)].map((_, i) => (
                            <div
                              key={i}
                              className="w-5 h-5 rotate-45 border-2 transition-all hover:scale-110 hover:rotate-90"
                              style={{ borderColor: accentDark }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Botón cerrar */}
                <div className="flex justify-center mt-6">
                  <button
                    onClick={onClose}
                    className="group px-6 py-2.5 bg-white/10 backdrop-blur-md text-white rounded-xl hover:bg-white/20 transition-all duration-300 shadow-lg hover:shadow-xl border border-white/20"
                  >
                    Cerrar
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CredencialCard;
